import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api.client';
import type { ApiResponse } from '@/types/api.types';

export interface VoiceUsageSummary {
  total_sessions: number;
  active_sessions: number;
  total_minutes: number;
  total_participant_minutes: number;
  unique_participants: number;
}

export interface SessionTypeUsageItem {
  sessionType: string;
  sessions: number;
  totalMinutes: number;
  participants: number;
}

export interface VoiceUserUsageItem {
  userId: string;
  displayName: string;
  email: string;
  sessionCount: number;
  totalMinutes: number;
}

const buildParams = (dateFrom?: string, dateTo?: string) => {
  const params: Record<string, string> = {};
  if (dateFrom) params.from = dateFrom;
  if (dateTo) params.to = dateTo;
  return params;
};

export const useVoiceUsageSummary = (dateFrom?: string, dateTo?: string, enabled = true) => {
  return useQuery({
    queryKey: ['voice-usage', 'summary', dateFrom, dateTo],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<VoiceUsageSummary>>(
        '/admin/voice-usage/summary',
        { params: buildParams(dateFrom, dateTo) },
      );
      return data.data!;
    },
    staleTime: 30_000,
    enabled,
  });
};

export const useVoiceUsageByType = (dateFrom?: string, dateTo?: string, enabled = true) => {
  return useQuery({
    queryKey: ['voice-usage', 'by-type', dateFrom, dateTo],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<SessionTypeUsageItem[]>>(
        '/admin/voice-usage/by-type',
        { params: buildParams(dateFrom, dateTo) },
      );
      return data.data!;
    },
    staleTime: 30_000,
    enabled,
  });
};

export const useVoiceUsageByUser = (dateFrom?: string, dateTo?: string, enabled = true) => {
  return useQuery({
    queryKey: ['voice-usage', 'by-user', dateFrom, dateTo],
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<VoiceUserUsageItem[]>>(
        '/admin/voice-usage/by-user',
        { params: buildParams(dateFrom, dateTo) },
      );
      return data.data!;
    },
    staleTime: 30_000,
    enabled,
  });
};
